// src/hooks/useRankedFeed.js
import { useState, useEffect } from 'react';

// The AI-ranked "Glance" feed is a static JSON file regenerated hourly by a
// cron, served from a different origin than the main API. It is NOT the
// parametrized api.govglance.org endpoint, so it gets its own hook rather than
// going through useFeedData.
//
// Expected shape:
//   { country, generatedAt, source, count,
//     items: [{ id, rank, importance, title, url, source, collectionName,
//               description, publishedAt }] }
const RANKED_FEED_BASE = 'https://agent.govglance.org/api/feed';

const SUPPORTED_COUNTRIES = ['us', 'uk', 'canada', 'mexico'];

// How far past generatedAt an item may be dated before it's treated as bad
// data. Covers minor clock skew between the sources and the cron host.
const FUTURE_TOLERANCE_MS = 5 * 60 * 1000;

// A date-time with no Z or offset. Browsers parse these as LOCAL time, so a
// UTC value written by Python's datetime.utcnow().isoformat() shows up hours
// in the future for anyone west of UTC. The cron writes UTC, so pin it.
// Date-only strings ("2026-09-28") are left alone: those already parse as UTC.
const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;
const asUtc = (value) => (
  typeof value === 'string' && NAIVE_ISO_RE.test(value) ? `${value}Z` : value
);

// Remap a ranked item onto the post shape FeedItem already understands.
// Adjust the target keys here if FeedItem reads different field names.
const normalizeRankedItem = (it) => ({
  ...it,
  created_at: asUtc(it.publishedAt || it.created_at || null),
  collection_name: it.collectionName || it.collection_name || it.source,
  link: it.url || it.link,
});

// Tolerate a bare array, the documented { items: [...] } envelope, or any of
// the other envelope keys the rest of the app uses.
const extractRankedItems = (parsed) => {
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === 'object') {
    if (Array.isArray(parsed.items)) return parsed.items;
    for (const key of ['results', 'posts', 'data', 'recent', 'documents']) {
      if (Array.isArray(parsed[key])) return parsed[key];
    }
  }
  return [];
};

// Nothing can have been published after the file that lists it was generated.
// Measured against the file's own generatedAt rather than the reader's clock,
// so a device with a wrong clock can't hide valid items. Falls back to now
// when the envelope has no generatedAt (bare-array payloads). Items with no
// parseable date are kept; there's nothing to judge them by.
const getCutoffMs = (parsed) => {
  const generated = Date.parse(asUtc(parsed && parsed.generatedAt));
  return Number.isNaN(generated) ? Date.now() : generated;
};

const isNotFuture = (item, cutoffMs) => {
  const t = item.created_at ? Date.parse(item.created_at) : NaN;
  return Number.isNaN(t) || t <= cutoffMs + FUTURE_TOLERANCE_MS;
};

/**
 * Fetches the AI-ranked Glance feed for a country.
 *
 * Any failure mode (network error, non-OK status, JSON parse failure, or an
 * empty/invalid payload, including one where every item is future-dated)
 * surfaces as a non-null `error`. The home page uses that signal to fall back
 * to the Recents view.
 *
 * @param {string} country - one of 'us' | 'uk' | 'canada' | 'mexico'
 * @returns {{ items: Array, loading: boolean, error: string | null }}
 */
const useRankedFeed = (country = 'us') => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const safeCountry = SUPPORTED_COUNTRIES.includes(country) ? country : 'us';

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${RANKED_FEED_BASE}/${safeCountry}.json`, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
          // No Authorization header: this is a static public file on a
          // different origin. Sending the API key would force a CORS preflight.
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        // Read as text and parse manually so a 200 that returns an HTML error
        // page lands in the catch and triggers the Recents failsafe rather than
        // throwing somewhere less predictable.
        const raw = await res.text();
        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch {
          throw new Error('ranked-feed-parse-failed');
        }

        const cutoffMs = getCutoffMs(parsed);
        const normalized = extractRankedItems(parsed).map(normalizeRankedItem);
        const list = normalized.filter((item) => isNotFuture(item, cutoffMs));

        if (list.length < normalized.length && process.env.NODE_ENV !== 'production') {
          console.warn(
            `useRankedFeed: dropped ${normalized.length - list.length} future-dated item(s) from ${safeCountry}.json`
          );
        }

        if (list.length === 0) throw new Error('ranked-feed-empty');

        if (!cancelled) setItems(list);
      } catch (err) {
        if (!cancelled && err.name !== 'AbortError') {
          setItems([]);
          setError(err.message || 'ranked-feed-error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; controller.abort(); };
  }, [country]);

  return { items, loading, error };
};

export default useRankedFeed;